import React, { useState } from 'react';
import { AxiosError } from "axios";

import { Constants } from "@eagerdog/constants";

import { Modal, useModal } from "src/components/Modal/Modal";
import ContextMenu/*, { IContextMenuOption }*/ from "src/components/ContextMenu/ContextMenu";
import { download } from "src/components/Download/DownloadManager";
import { toast } from "src/components/Toast/ToastManager";

import EditRegistration from "src/components/EditRegistration/EditRegistration";
import ChangeElements from "src/components/ChangeElements/ChangeElements";
import Confirm from "src/components/Confirm/Confirm";

import { apiService } from "../../../services";
import type { CustomCellRendererProps } from 'ag-grid-react';

import styles from "./RegistrantsTab.module.scss";
import SendMessage, { SendMessageType } from "../../../components/SendMessage/SendMessage";
import { IEventMessage } from "@eagerdog/interfaces";

interface CustomRegistrantCellRendererProps extends CustomCellRendererProps {
  getRegistrants(): void
}

enum ActionsRendererMenuOptions {
  Edit = "edit",
  Change = "change",
  Remove = "remove",
  ResendRegistration = "resend",
}

export const ActionsRenderer = (props: CustomRegistrantCellRendererProps) => {
  const { isShown, toggle } = useModal();
  const [isSending, setIsSending] = useState<boolean>(false);

  const [modalContent, setModalContent] = useState<ActionsRendererMenuOptions | undefined>();
  const [removingDog, setRemovingDog] = useState<boolean>(false);

  const removeDog = () => {
    setRemovingDog(true);

    apiService.removeDogFromShow(props.value.event._id, props.value.registration.show_id, props.value.registration.dog.dog_id).then((response) => {
      toast.show({
        title: "Remove Dog",
        content: "Successfully removed " + props.value.registration.dog.call_name + " from " + props.value.registration.show_type + " " + props.value.registration.show_name,
        duration: 10000,
        type: "success"
      });

      toggle();
      setModalContent(undefined);
      props.getRegistrants();
    }).catch((e: AxiosError) => {
      toast.show({
        title: "Remove Dog",
        content: "Unable to remove dog",
        duration: 10000,
        errorDetails: e,
        type: "fail"
      });
    }).finally(() => {
      setRemovingDog(false);
    });
  }

  const resendRegistration = (payload: IEventMessage) => {
    setIsSending(true)
    apiService.requestEntryForms(
      props.value.registration.event_id, props.value.registration.dog.dog_id, true, payload
    )
      .then(() => {
        const content = payload.is_test_sending
          ? `Test email sent to ${props.value.event.club.email}.`
          : `Successfully sent email to ${props.value.registration.owner.email}`
        toast.show({
          title: "Registration Form",
          content,
          duration: 10000,
          type: "success"
        });
      })
      .catch((error) => {
        toast.show({
          title: "Registration Form",
          content: `Unable to send email to ${props.value.registration.owner.email}`,
          duration: 10000,
          errorDetails: error,
          type: "fail"
        });
      })
      .finally(() => {
        setIsSending(false)
        toggle();
        setModalContent(undefined);
      });
  }

  const getModalContent = () => {
    switch(modalContent) {
      case ActionsRendererMenuOptions.Edit:
        return(<EditRegistration
          event={props.value.event}
          registration={props.value.registration}
          onComplete={() => {
            toggle();
            setModalContent(undefined);
            props.getRegistrants(); }}
            />
          );
      case ActionsRendererMenuOptions.Change:
        return (<ChangeElements
          event={props.value.event}
          registration={props.value.registration}
          onComplete={() => {
            toggle();
            setModalContent(undefined);
            props.getRegistrants();
          }}
          onClose={() => {
            toggle();
            setModalContent(undefined);
          }}
        />);
      case ActionsRendererMenuOptions.Remove:
        return(<Confirm
          message={<><p>Are you sure you want to remove {props.value.registration.dog.call_name} from {props.value.registration.show_type} {props.value.registration.show_name}? This won't refund the transaction, you can refund on the Transactions tab.</p></>}
          buttonsDisabled={removingDog}
          onConfirm={() => {
            removeDog();
          }}
          onCancel={() => {
            toggle();
            setModalContent(undefined);
          }}
        />);
      case ActionsRendererMenuOptions.ResendRegistration:
        return(<SendMessage
          isSending={isSending}
          handleSendMessage={resendRegistration}
          subject={`Your Updated Registration Form for ${props.value.event.title}`}
          messageBody={`
            <p>The registration form(s) for <strong>${props.value.registration.dog.call_name}</strong> in 
            <strong>${props.value.event.title}</strong>, hosted by <strong>${props.value.event.club_name}</strong>, has 
            been resent to you. This may be due to a modification to your registration or because you requested a copy.</p>
            <p>Please review the attached form carefully to ensure all details are correct. If you notice any 
            discrepancies or need further changes, please contact the event organizers as soon as possible.</p>
            <p>We look forward to seeing you at the event! 🐶🏆</p>
          `}
          type={SendMessageType.ResendRegistration}
        />);
    }

    return <></>;
  }

  const getModalClassName = () => {
    switch (modalContent) {
      case ActionsRendererMenuOptions.Edit:
        return "editRegistrationModal";
      case ActionsRendererMenuOptions.Change:
        return "changeElementsModal";
      case ActionsRendererMenuOptions.Remove:
        return "removeDogModal";
      case ActionsRendererMenuOptions.ResendRegistration:
        return "sendMessageModal";
    }

    return "";
  }

  const menuOptions = [
    {
      label: "Edit Registration",
      onClick:() => {
        toggle();
        setModalContent(ActionsRendererMenuOptions.Edit);
      }
    },
    {
      label: "Change Elements",
      onClick: () => {
        toggle();
        setModalContent(ActionsRendererMenuOptions.Change);
      }
    },
    {
      label: "Remove Dog",
      onClick: () => {
        toggle();
        setModalContent(ActionsRendererMenuOptions.Remove);
      }
    }
  ];

  if (props.value.event.sanctioning_club !== Constants.sanctioning_club.PWDCA) {
    menuOptions.unshift(
      {
        label: "Download Registration Form(s)",
        onClick:() => {
          download.pushDownload({
            type: "registration",
            registrantId: props.value.registration.event_id,
            dogId: props.value.registration.dog.dog_id
          });
        }
      },
      {
        label: 'Re-send Registration Form(s)',
        onClick: () => {
          toggle();
          setModalContent(ActionsRendererMenuOptions.ResendRegistration);
        }
      },
    )
  }
  return (<div className={styles.ActionRenderer}>
    <ContextMenu
      options={menuOptions}
    />
    <Modal
      className={getModalClassName()}
      isShown={isShown}
      hide={() => { toggle(); setModalContent(undefined); }}
      modalContent={getModalContent()}
    />
  </div>);
};
